import { Fragment, useCallback } from 'react';
import {
  QueryFunctionContext,
  useMutation,
  useQueryClient,
  UseInfiniteQueryOptions,
  InfiniteData,
} from 'react-query';
import { NOTIFICATION_STATUSES, useNotifications } from '@adsk/alloy-react-notification';
import * as analytics from '@adsk/acs-web-analytics';
import { T } from '../../helpers/i18n';
import {
  addRfiTypeTrackEvent,
  deleteRfiTypeTrackEvent,
  editRfiTypeTrackEvent,
} from '../../helpers/analytics-helpers';
import {
  normalizeRfiTypeReadPayload,
  normalizeRfiTypeWritePayload,
  optimisticUpdateOnCreateSuccess,
  optimisticUpdateOnUpdateMutate,
} from './helpers/rfiTypesQueriesHelpers';
import { useSDK } from '../useSDK';
import { EntityStatus, GetRfiTypesResponse, NewRfiType, RfiType } from '../../sdk';
import { useFetchAllPages } from './useFetchAllPages';
import featureFlags from '../../helpers/featureFlags';
import { FEATURE_FLAGS } from '../../constants/featureFlags';

const RFI_TYPES_QUERY_KEY = 'rfi-types-v3';
const FETCH_LIMIT = 200;
const FIVE_MINUTES = 5 * 60 * 1000;
const queryKey = [RFI_TYPES_QUERY_KEY];

export const useInvalidateRfiTypes = () => {
  const queryClient = useQueryClient();
  return useCallback(() => queryClient.invalidateQueries([RFI_TYPES_QUERY_KEY]), [queryClient]);
};

export const useGetRfiTypes = (
  options?: UseInfiniteQueryOptions<GetRfiTypesResponse, unknown>,
  limit = FETCH_LIMIT,
) => {
  const sdk = useSDK();
  const queryFn = useCallback(
    ({ pageParam = { limit, offset: 0 } }: QueryFunctionContext) =>
      sdk.RfiTypes.get({ ...pageParam }).then((response) => {
        const isOptionalReviewersEnabled = !!featureFlags.get(FEATURE_FLAGS.OPTIONAL_REVIEWERS);
        return {
          ...response,
          results: response.results.map((rfiType) =>
            isOptionalReviewersEnabled ? normalizeRfiTypeReadPayload(rfiType) : rfiType,
          ),
        };
      }),
    [sdk, limit],
  );
  return useFetchAllPages({
    ...options,
    queryKey,
    queryFn,
    staleTime: FIVE_MINUTES,
  });
};

export const useCreateRfiType = () => {
  const sdk = useSDK();
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();

  return useMutation(
    (rfiType: NewRfiType) =>
      sdk.RfiTypes.add(
        normalizeRfiTypeWritePayload(rfiType, !!featureFlags.get(FEATURE_FLAGS.OPTIONAL_REVIEWERS)),
      ),
    {
      onError: () => {
        showNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: T.translate('rfiTypeFailedToCreate'),
        });
      },
      onSuccess: (newRfiType) => {
        // Update state with server data
        const previousState = queryClient.getQueryData<GetRfiTypesResponse>([RFI_TYPES_QUERY_KEY]);

        if (previousState) {
          const updatedResults = optimisticUpdateOnCreateSuccess({ previousState, newRfiType });

          queryClient.setQueryData<GetRfiTypesResponse>([RFI_TYPES_QUERY_KEY], {
            ...previousState,
            results: updatedResults,
          });
        }

        showNotification({
          status: NOTIFICATION_STATUSES.SUCCESS,
          message: (
            <Fragment>
              <span style={{ fontWeight: 'bold' }}>{`${newRfiType.name} `}</span>
              <span>{T.translate('successfullyCreated')}</span>
            </Fragment>
          ),
        });

        analytics.track(...addRfiTypeTrackEvent(newRfiType));
      },
      onSettled: () => {
        queryClient.invalidateQueries([RFI_TYPES_QUERY_KEY]); // Refetch from server
      },
    },
  );
};
export const useUpdateRfiType = () => {
  const sdk = useSDK();
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();

  return useMutation(
    ({ id, data }: { id: string; data: Partial<RfiType> }) =>
      sdk.RfiTypes.update(
        id,
        normalizeRfiTypeWritePayload(data, !!featureFlags.get(FEATURE_FLAGS.OPTIONAL_REVIEWERS)),
      ),
    {
      // Happen before the POST request is sent
      onMutate: async ({ id: updatedRfiTypeId, data: updatedRfiType }) => {
        await queryClient.cancelQueries([RFI_TYPES_QUERY_KEY]);
        const previousState = queryClient.getQueryData<InfiniteData<GetRfiTypesResponse>>([
          RFI_TYPES_QUERY_KEY,
        ]);

        if (previousState) {
          const updatedState = optimisticUpdateOnUpdateMutate({
            previousState,
            updatedRfiType,
            updatedRfiTypeId,
          });

          queryClient.setQueryData<InfiniteData<GetRfiTypesResponse>>(
            [RFI_TYPES_QUERY_KEY],
            updatedState,
          );
        }

        return previousState;
      },
      onError: (_err, _variables, previousState) => {
        if (previousState) {
          queryClient.setQueryData<InfiniteData<GetRfiTypesResponse>>(
            [RFI_TYPES_QUERY_KEY],
            previousState as InfiniteData<GetRfiTypesResponse>,
          );
          queryClient.setQueryData([RFI_TYPES_QUERY_KEY], previousState);

          showNotification({
            status: NOTIFICATION_STATUSES.ERROR,
            message: T.translate('new.error.general'),
          });
        }
      },
      onSuccess: (rfiType) => {
        const analyticEvent =
          rfiType.status === EntityStatus.Inactive
            ? deleteRfiTypeTrackEvent(rfiType)
            : editRfiTypeTrackEvent(rfiType);
        analytics.track(...analyticEvent);
      },
      onSettled: () => {
        queryClient.invalidateQueries([RFI_TYPES_QUERY_KEY]);
      },
    },
  );
};
